import { useEffect, useRef, useState, type FormEvent } from "react";
import { getSocket } from "../lib/socket";
import { useAuth } from "../lib/auth";
import type { Message } from "../lib/types";

interface Props {
  deviceId: string;
  clientId: string;
}

/**
 * Live technician <-> device chat over Socket.io. History is not persisted by a
 * REST endpoint in the current API, so this shows messages from the moment the
 * panel opens.
 */
export default function ChatPanel({ deviceId, clientId }: Props) {
  const { staff } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [body, setBody] = useState("");
  const [userTyping, setUserTyping] = useState(false);
  const [userOnline, setUserOnline] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const socket = getSocket();

    const onMessage = (m: Message) => {
      if (m.deviceId === deviceId) setMessages((prev) => [...prev, m]);
    };
    const onTyping = (p: { deviceId: string }) => {
      if (p.deviceId === deviceId) setUserTyping(true);
    };
    const onTypingStop = (p: { deviceId: string }) => {
      if (p.deviceId === deviceId) setUserTyping(false);
    };
    const onOnline = (p: { deviceId: string }) => {
      if (p.deviceId === deviceId) setUserOnline(true);
    };
    const onOffline = (p: { deviceId: string }) => {
      if (p.deviceId === deviceId) setUserOnline(false);
    };
    const onSuggestion = (p: { suggestion: string; deviceId: string; error?: string }) => {
      if (p.deviceId && p.deviceId !== deviceId) return;
      setAiBusy(false);
      if (p.suggestion) setBody(p.suggestion);
    };

    socket.on("message:new", onMessage);
    socket.on("typing:user", onTyping);
    socket.on("typing:user:stop", onTypingStop);
    socket.on("agent:online", onOnline);
    socket.on("agent:offline", onOffline);
    socket.on("ai:suggestion", onSuggestion);

    return () => {
      socket.off("message:new", onMessage);
      socket.off("typing:user", onTyping);
      socket.off("typing:user:stop", onTypingStop);
      socket.off("agent:online", onOnline);
      socket.off("agent:offline", onOffline);
      socket.off("ai:suggestion", onSuggestion);
    };
  }, [deviceId]);

  useEffect(() => {
    logRef.current?.scrollTo(0, logRef.current.scrollHeight);
  }, [messages, userTyping]);

  function send(e: FormEvent) {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    getSocket().emit("staff:message", { body: text, deviceId, clientId });
    getSocket().emit("typing:stop", { deviceId });
    setBody("");
  }

  function requestAi() {
    setAiBusy(true);
    getSocket().emit("ai:suggest", { deviceId, clientId });
  }

  function requestRemote() {
    getSocket().emit("remote:request", {
      deviceId,
      techName: staff?.name ?? "Technician",
    });
  }

  return (
    <div className="card chat">
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Chat</h2>
        <span className={`pill dot ${userOnline ? "ok" : ""}`}>
          {userOnline ? "user in chat" : "user idle"}
        </span>
        <div className="spacer" />
        <button onClick={requestRemote}>Request remote</button>
      </div>

      <div className="log" ref={logRef}>
        {messages.length === 0 && (
          <div className="muted" style={{ margin: "auto" }}>
            No messages yet.
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`msg ${m.fromUser ? "" : "mine"}`}>
            <div>{m.body}</div>
            <div className="meta">
              {m.fromUser ? "User" : "You"} ·{" "}
              {new Date(m.createdAt).toLocaleTimeString()}
            </div>
          </div>
        ))}
        {userTyping && <div className="muted">User is typing…</div>}
      </div>

      <form onSubmit={send}>
        <input
          value={body}
          placeholder="Type a reply…"
          onChange={(e) => {
            setBody(e.target.value);
            getSocket().emit("typing:start", { deviceId });
          }}
          onBlur={() => getSocket().emit("typing:stop", { deviceId })}
        />
        <button type="button" onClick={requestAi} disabled={aiBusy}>
          {aiBusy ? "Drafting…" : "AI draft"}
        </button>
        <button className="primary" type="submit">
          Send
        </button>
      </form>
    </div>
  );
}
